import {
  CampaignStatusType,
  IAdminCampaign,
  toCurrencyFormat,
  fallbackText,
  HelpTypes,
} from '@pharmaplan/common';

import { DynamicTableCell, UserLabels } from '../../../helpers/Constants';
import { getDayMonthDateYear } from '../../Reports/helpers';
import editIcon from '../../../assets/svg/updateNoteIcon.svg';
import deleteIcon from '../../../assets/svg/delete.svg';
import historyIcon from '../../../assets/svg/showPassword.svg';
import linkedUser from '../../../assets/svg/linkedUsers.svg';
import strings from '../../../localization';

const { text, chip, bar } = DynamicTableCell;

export const campaignStatusMap = {
  [CampaignStatusType.Draft]: {
    color: 'common.grey850',
    label: strings.draft,
  },
  [CampaignStatusType.Active]: {
    color: 'primary.main',
    label: strings.active,
  },
  [CampaignStatusType.Inactive]: {
    color: 'common.yellow700',
    label: strings.inactive,
  },
};

const safeDate = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  return getDayMonthDateYear(date);
};

interface ICampaignRowsParams {
  data: Array<IAdminCampaign>;
  handleEdit: (id: string) => () => void;
  handleDelete: (id: string) => () => void;
  handleViewBookings: (id: string) => () => void;
  handleViewLinkedLeads: (id: string, userType: HelpTypes) => () => void;
}

export const campaignHeaders = (userType: HelpTypes) => {
  let sortingKey = '';
  if (userType === HelpTypes.pharmacy) {
    sortingKey = 'promoHourlyRate';
  } else if ([HelpTypes.students, HelpTypes.pharmacist, HelpTypes.technicians].includes(userType)) {
    sortingKey = 'hourlyRateBonus';
  }

  return [
    {
      key: '0',
      label: strings.name,
      sortingKey: 'name',
    },
    {
      key: 'userType',
      label: strings.userType,
      sortingKey: 'userType',
    },
    {
      key: '1',
      label: `${strings.promoHourlyRate} / ${strings.bonus}`,
      sortingKey,
      isCenterAligned: true,
    },
    {
      key: '2',
      label: strings.totalCredits,
      sortingKey: 'creditShiftCount',
      isCenterAligned: true,
    },
    {
      key: '3',
      label: strings.campaignStartDate,
      sortingKey: 'startDate',
    },
    {
      key: '4',
      label: strings.campaignEndDate,
      sortingKey: 'endDate',
    },
    {
      key: '5',
      label: strings.creditsValidUntil,
      sortingKey: 'validityUntil',
    },
    {
      key: '6',
      label: strings.status,
      sortingKey: 'status',
    },
    {
      key: '7',
      label: strings.action,
    },
  ];
};

export const campaignRows = ({
  data,
  handleEdit,
  handleDelete,
  handleViewBookings,
  handleViewLinkedLeads,
}: ICampaignRowsParams) =>
  data?.map((item) => {
    const {
      campaignId,
      name,
      promoHourlyRate,
      creditShiftCount,
      startDate = '',
      hourlyRateBonus,
      endDate = '',
      validityUntil = '',
      status,
      userType,
    } = item ?? {};

    const isPharmacy = `${userType}` === HelpTypes.pharmacy;

    const actionItems = [
      {
        key: '1',
        icon: editIcon,
        tooltip: strings.edit,
        onClick: handleEdit(campaignId),
      },
      {
        key: '2',
        icon: deleteIcon,
        tooltip: strings.delete,
        onClick: handleDelete(campaignId),
      },
      status !== CampaignStatusType.Draft && {
        key: '4',
        icon: linkedUser,
        tooltip: strings.viewLinkedLeads,
        onClick: handleViewLinkedLeads(campaignId, `${userType}` as HelpTypes),
      },
      {
        key: '3',
        icon: historyIcon,
        tooltip: strings.viewBookings,
        onClick: handleViewBookings(campaignId),
      },
    ];

    return {
      key: campaignId,
      data: [
        {
          key: '0',
          value: name,
          type: text,
        },
        {
          key: 'userType',
          value: userType ? UserLabels()?.[userType] : fallbackText,
          type: text,
        },
        {
          key: '1',
          value: isPharmacy
            ? toCurrencyFormat(promoHourlyRate ?? 0)
            : toCurrencyFormat(hourlyRateBonus ?? 0),
          type: text,
          isCenterAligned: true,
        },
        {
          key: '2',
          value: creditShiftCount,
          type: text,
          isCenterAligned: true,
        },
        {
          key: '3',
          value: safeDate(startDate),
          type: text,
        },
        {
          key: '4',
          value: safeDate(endDate),
          type: text,
        },
        {
          key: '5',
          value: getDayMonthDateYear(validityUntil ?? ''),
          type: text,
        },
        {
          key: '6',
          value: {
            label: campaignStatusMap[status].label,
            style: { color: campaignStatusMap[status].color },
          },
          type: chip,
        },
        {
          key: '7',
          value: actionItems,
          type: bar,
        },
      ],
    };
  });
